import { useState, useEffect } from "react";

import { useAppBridge } from "../hooks/useAppBridge";
import { useApi } from "../hooks/useApi";
import { createSubmissionsApi } from "../api/submissions";
import { useTranslation } from "../hooks/useTranslation";

import {
  Card,
  DataTable,
  SkeletonBodyText,
  Banner,
  EmptyState,
  Button,
  Badge,
  BlockStack,
  Text,
  Link,
  InlineStack,
  Box,
} from "@shopify/polaris";

interface SubmissionsPageProps {
  shop: string | null;
}

interface Submission {
  id: string;
  order_id: string;
  order_number: string;
  mydata_mark?: string;
  mydata_uid?: string;
  status: string;
  submission_type?: string;
  created_at: string;
  customer_name?: string;
}

interface OrderGroup {
  order_id: string;
  order_number: string;
  customer_name?: string;
  submissions: Submission[];
}

export default function SubmissionsPage({ shop }: SubmissionsPageProps) {
  const { t } = useTranslation();
  const { app } = useAppBridge();
  const { Call } = useApi({ baseUrl: "/api" });
  const submissionsApi = createSubmissionsApi(Call);

  const [orders, setOrders] = useState<OrderGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingPdfForOrder, setLoadingPdfForOrder] = useState<string | null>(null);
  const perPage = 20;

  const fetchSubmissions = async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      console.log("[SubmissionsPage] Fetching submissions list (page", page, ")...");
      const response = await submissionsApi.list(page, perPage);
      console.log(
        "[SubmissionsPage] Fetched submissions response:",
        response,
      );

      if (response && response.success && response.data) {
        setOrders(response.data.orders || []);
        setCurrentPage(response.data.page);
        setTotalPages(response.data.total_pages);
        setTotalCount(response.data.total_count);
      } else {
        console.error("[SubmissionsPage] Failed response:", response);
        setError(
          response?.message ||
            response?.error ||
            "Failed to load submissions",
        );
      }
    } catch (err) {
      console.error("[SubmissionsPage] Error fetching submissions:", err);
      setError(
        `An error occurred while loading submissions: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!app) return;
    fetchSubmissions(currentPage);
  }, [app]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString("el-GR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "success":
        return <Badge tone="success">{t("submissions.status.success")}</Badge>;
      case "failure":
        return <Badge tone="critical">{t("submissions.status.failure")}</Badge>;
      case "cancelled":
        return <Badge tone="warning">{t("submissions.status.cancelled")}</Badge>;
      case "pending":
        return <Badge tone="attention">{t("submissions.status.pending")}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const handlePdfDownload = async (orderId: string) => {
    setLoadingPdfForOrder(orderId);
    try {
      console.log("[SubmissionsPage] Opening PDF for order:", orderId);

      // Step 1: Get submission data
      const submissionResponse = await Call(
        `/submissions/${encodeURIComponent(orderId)}`,
        "GET",
      );
      if (
        !submissionResponse ||
        !submissionResponse.success ||
        !submissionResponse.data
      ) {
        throw new Error("Failed to fetch submission data");
      }
      const submission = submissionResponse.data;
      console.log("[SubmissionsPage] Got submission data:", submission);

      // Step 2: Get shop settings
      const settingsResponse = await Call("/settings", "GET");
      if (
        !settingsResponse ||
        !settingsResponse.success ||
        !settingsResponse.data
      ) {
        throw new Error("Failed to fetch shop settings");
      }
      const shopSettings = settingsResponse.data;
      console.log("[SubmissionsPage] Got shop settings");

      // Step 3: Get PDF service token
      const tokenResponse = await Call(
        "/service-token?service=pdf-service",
        "GET",
      );
      if (!tokenResponse || !tokenResponse.success || !tokenResponse.data) {
        throw new Error("Failed to get PDF service token");
      }
      const pdfServiceToken = tokenResponse.data.token;
      console.log("[SubmissionsPage] Got PDF service token");

      // Step 4: Call PDF service with token
      // Use environment variable or default to localhost

      const pdfServiceUrl =
        `${import.meta.env.VITE_PDF_SERVICE_URL}` || "no-service-url";
      console.log("[SubmissionsPage] PDF service URL:", pdfServiceUrl);

      // Build headers with service token
      const pdfHeaders: HeadersInit = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${pdfServiceToken}`,
      };

      // Add ngrok header only in development
      if (import.meta.env.MODE === "development") {
        pdfHeaders["ngrok-skip-browser-warning"] = "true";
      }

      const pdfResponse = await fetch(
        `${pdfServiceUrl}/api/generate-delivery-note-pdf`,
        {
          method: "POST",
          headers: pdfHeaders,
          body: JSON.stringify({
            submission,
            shopSettings,
          }),
        },
      );

      if (!pdfResponse.ok) {
        const errorText = await pdfResponse.text();
        throw new Error(
          `PDF generation failed: ${pdfResponse.statusText} - ${errorText}`,
        );
      }

      console.log("[SubmissionsPage] PDF generated successfully");

      // Step 5: Open PDF in new tab
      const blob = await pdfResponse.blob();
      const pdfUrl = window.URL.createObjectURL(blob);
      window.open(pdfUrl, "_blank");
      // Don't revoke immediately - give browser time to open
      setTimeout(() => window.URL.revokeObjectURL(pdfUrl), 1000);

      console.log("[SubmissionsPage] PDF opened in new tab");
    } catch (err) {
      console.error("[SubmissionsPage] Error opening PDF:", err);
      alert(
        `Failed to open PDF: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    } finally {
      setLoadingPdfForOrder(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <BlockStack gap="400">
          <SkeletonBodyText lines={8} />
        </BlockStack>
      </Card>
    );
  }

  if (error) {
    return (
      <Banner tone="critical" title="Error">
        {error}
      </Banner>
    );
  }

  if (orders.length === 0) {
    return (
      <Card>
        <EmptyState
          heading={t("submissions.empty.title")}
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        >
          <p>{t("submissions.empty.description")}</p>
        </EmptyState>
      </Card>
    );
  }

  // Helper to construct Shopify admin order URL
  const getOrderUrl = (orderId: string) => {
    if (!shop) return null;

    // Extract numeric ID from GID if needed
    const numericId = orderId.includes("gid://")
      ? orderId.split("/").pop()
      : orderId;

    // Extract shop name (remove .myshopify.com)
    const shopName = shop.replace(".myshopify.com", "");

    return `https://admin.shopify.com/store/${shopName}/orders/${numericId}`;
  };

  const getSubmissionTypeLabel = (type?: string) => {
    switch (type) {
      case "delivery_note":
        return t("submissions.types.delivery_note");
      case "receipt":
        return t("submissions.types.receipt");
      case "receipt_with_delivery_note":
        return t("submissions.types.receipt_with_delivery_note");
      case "invoice":
        return t("submissions.types.invoice");
      case "invoice_with_delivery_note":
        return t("submissions.types.invoice_with_delivery_note");
      case "invoice_on_behalf":
        return t("submissions.types.invoice_on_behalf");
      case "clearance_invoice":
        return t("submissions.types.clearance_invoice");
      case "receipt_on_behalf":
        return t("submissions.types.receipt_on_behalf");
      case "credit_invoice":
        return t("submissions.types.credit_invoice");
      case "retail_credit_note":
        return t("submissions.types.retail_credit_note");
      default:
        return type || "—";
    }
  };

  const getDocumentsCountLabel = (count: number) =>
    t(`submissions.documentsCount.${count === 1 ? "one" : "other"}`, {
      count,
    });

  // One "header" row per order (order number + customer + document count),
  // followed by one indented row per submission event for that order, oldest
  // first — the order groups the rows, the events show its full history.
  const rows = orders.flatMap((order) => {
    const orderUrl = getOrderUrl(order.order_id);
    const headerRow = [
      <BlockStack gap="050" key={`${order.order_id}-header`}>
        <Text variant="bodyMd" fontWeight="bold" as="span">
          {orderUrl ? (
            <Link url={orderUrl} external removeUnderline>
              {order.order_number || "N/A"}
            </Link>
          ) : (
            order.order_number || "N/A"
          )}
        </Text>
        {order.customer_name && (
          <Text variant="bodySm" tone="subdued" as="span">
            {order.customer_name}
          </Text>
        )}
      </BlockStack>,
      getDocumentsCountLabel(order.submissions.length),
      "",
      "",
      "",
      "",
    ];

    const submissionRows = order.submissions.map((submission) => [
      <Box paddingInlineStart="400" key={submission.id}>
        <Text variant="bodySm" tone="subdued" as="span">
          ↳
        </Text>
      </Box>,
      getSubmissionTypeLabel(submission.submission_type),
      formatDate(submission.created_at),
      getStatusBadge(submission.status),
      submission.mydata_mark || "N/A",
      submission.status === "success" ? (
        <Button
          onClick={() =>
            handlePdfDownload(submission.order_id)
          }
          size="slim"
          loading={loadingPdfForOrder === submission.order_id}
          disabled={
            loadingPdfForOrder !== null &&
            loadingPdfForOrder !== submission.order_id
          }
        >
          {t("submissions.actions.viewPdf")}
        </Button>
      ) : (
        "—"
      ),
    ]);

    return [headerRow, ...submissionRows];
  });

  const handleNextPage = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchSubmissions(nextPage);
  };

  const handlePreviousPage = () => {
    const prevPage = currentPage - 1;
    setCurrentPage(prevPage);
    fetchSubmissions(prevPage);
  };

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="200">
            <Text variant="headingMd" as="h2">
              {t("submissions.title")}
            </Text>
            <Text variant="bodySm" as="p" tone="subdued">
              {t("submissions.subtitle")}
            </Text>
          </BlockStack>
          {totalCount > 0 && (
            <Badge tone="info">
              {t("submissions.totalCount", { count: totalCount })}
            </Badge>
          )}
        </InlineStack>
        <DataTable
          columnContentTypes={["text", "text", "text", "text", "text", "text"]}
          headings={[
            t("submissions.columns.order"),
            t("submissions.columns.type"),
            t("submissions.columns.date"),
            t("submissions.columns.status"),
            t("submissions.columns.mark"),
            t("submissions.columns.pdf"),
          ]}
          rows={rows}
          pagination={{
            hasPrevious: currentPage > 1,
            hasNext: currentPage < totalPages,
            onNext: handleNextPage,
            onPrevious: handlePreviousPage,
          }}
          footerContent={t("submissions.pagination.footer", {
            page: currentPage,
            totalPages: totalPages,
            count: totalCount,
          })}
        />
      </BlockStack>
    </Card>
  );
}
